const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const normalizeString = (value) => (typeof value === 'string' ? value.trim() : '')
const normalizeAssignmentSource = (value) => normalizeString(value).toLowerCase()
const isTruthyBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1'

const resolveAdminLabel = (value, adminUserMap = {}) => {
  const normalizedValue = normalizeString(value)
  if (!normalizedValue) return null

  const mappedUsername = adminUserMap[normalizedValue]
  if (mappedUsername) {
    return `${mappedUsername} (${normalizedValue.substring(0, 5)})`
  }

  if (UUID_REGEX.test(normalizedValue)) {
    return `Admin (${normalizedValue.substring(0, 5)})`
  }

  return normalizedValue
}

const isReadableAssignedByLabel = (value) => {
  const normalizedValue = normalizeString(value)
  if (!normalizedValue) return false

  return normalizedValue.includes('(') && normalizedValue.includes(')')
}

const getAssignmentActionLabel = (assignmentType, assignmentSource) => {
  const normalizedType = normalizeString(assignmentType)
  const normalizedSource = normalizeAssignmentSource(assignmentSource)

  if (normalizedSource === 'n8n') {
    switch (normalizedType) {
      case 'manual':
        return 'n8n Manual'
      case 'reassignment':
        return 'n8n Reassignment'
      case 'round_robin':
        return 'Round Robin'
      case 'self_assigned':
        return 'Self Assigned'
      default:
        return normalizedType
          ? normalizedType
            .split('_')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
          : 'Unknown'
    }
  }

  switch (normalizedType) {
    case 'manual':
      return 'Dashboard Manual'
    case 'reassignment':
      return 'Dashboard Reassignment'
    case 'round_robin':
      return 'Round Robin'
    case 'self_assigned':
      return 'Self Assigned'
    default:
      return normalizedType
        ? normalizedType
          .split('_')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ')
        : 'Unknown'
  }
}

export const getAssignmentLogPresentation = (log, adminUserMap = {}) => {
  const assignmentType = normalizeString(log?.assignment_type)
  const consultationAssignmentSource = normalizeAssignmentSource(
    Array.isArray(log?.user_consultations)
      ? log.user_consultations[0]?.assignment_source
      : log?.user_consultations?.assignment_source
  )
  const persistedAssignmentSource = normalizeAssignmentSource(log?.assignment_source)
  const assignmentSource = persistedAssignmentSource && persistedAssignmentSource !== 'unknown'
    ? persistedAssignmentSource
    : (consultationAssignmentSource || persistedAssignmentSource)
  const assignedBy = normalizeString(log?.assigned_by)
  const reassignedBy = normalizeString(log?.reassigned_by)
  const notes = normalizeString(log?.notes).toLowerCase()
  const normalizedAssignedBy = assignedBy.toLowerCase()
  const hasExplicitMissingAdminFlag = isTruthyBoolean(log?.missing_admin_actor_id)
  const assignedByLabel = resolveAdminLabel(assignedBy, adminUserMap)
  const reassignedByLabel = resolveAdminLabel(reassignedBy, adminUserMap)

  const isDashboardAction = assignmentType === 'manual' || assignmentType === 'reassignment'
  const hasAdminUuid = UUID_REGEX.test(reassignedBy)
  const hasReadableAssignedBy = isReadableAssignedByLabel(assignedBy)
  const isKnownAuto = assignmentType === 'round_robin' || assignmentSource === 'n8n'
  const looksLikeLegacyDashboardUnknown =
    isDashboardAction &&
    !hasAdminUuid &&
    (
      normalizedAssignedBy === 'dashboard_unknown' ||
      normalizedAssignedBy === 'dashboard_missing_admin_uuid' ||
      normalizedAssignedBy === 'manual' ||
      !assignedBy ||
      notes.includes('manual from unassigned') ||
      notes.includes('reassignment from')
    )

  let sourceKind = 'unknown'
  let byLabel = null

  if (assignmentSource === 'dashboard') {
    if (hasAdminUuid) {
      sourceKind = 'dashboard_admin'
      byLabel = hasReadableAssignedBy ? assignedByLabel : (reassignedByLabel || assignedByLabel)
    } else if (hasReadableAssignedBy) {
      sourceKind = 'dashboard_admin'
      byLabel = assignedByLabel
    } else {
      sourceKind = 'dashboard_missing_admin'
      byLabel = 'Unknown admin'
    }
  } else if (assignmentSource === 'n8n') {
    sourceKind = 'automated'
    byLabel = assignmentType === 'round_robin' ? 'Round Robin' : 'n8n (auto)'
  } else if (hasExplicitMissingAdminFlag) {
    sourceKind = 'dashboard_missing_admin'
    byLabel = 'Unknown admin'
  } else if (hasAdminUuid) {
    sourceKind = 'dashboard_admin'
    byLabel = hasReadableAssignedBy ? assignedByLabel : (reassignedByLabel || assignedByLabel)
  } else if (hasReadableAssignedBy && isDashboardAction) {
    sourceKind = 'dashboard_admin'
    byLabel = assignedByLabel
  } else if (looksLikeLegacyDashboardUnknown) {
    sourceKind = 'dashboard_missing_admin'
    byLabel = 'Unknown admin'
  } else if (isKnownAuto) {
    sourceKind = 'automated'
    byLabel = 'n8n (auto)'
  } else if (assignedByLabel) {
    sourceKind = 'unknown'
    byLabel = assignedByLabel
  }

  return {
    actionLabel: getAssignmentActionLabel(assignmentType, assignmentSource),
    sourceKind,
    byLabel,
    isMissingAdminAttribution: sourceKind === 'dashboard_missing_admin',
  }
}

export { UUID_REGEX }
